import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { Button, Dropdown, Navbar } from "react-bootstrap";
import { all_routes } from "../feature-module/router/all_routes";
import { getUserDetails, isLoginUser } from "../services/user.service";
import {
  setIsRealEstate,
  setLogin,
  setUserDetail,
} from "../core/data/redux/user/userSlice";
import ImageWithBasePath from "../core/data/img/ImageWithBasePath";
import BellIcon from "../icons/BellIcon";
import UserIcon from "../icons/UserIcon";
import LockIcon from "../icons/LockIcon";
import DeleteGreyIcon from "../icons/DeleteGreyIcon";
import LogoutIcon from "../icons/LogoutIcon";
import LinkIcon from "../icons/LinkIcon";
import RingGreenIcon from "../icons/RingGreenIcon";
import Timer from "../icons/Timer";
import FileCircle from "../icons/FileCircle";
import { getStorageItem, setStorageItem } from "../services/storage.service";
import LogoutModal from "./logoutModal";
import DeleteModal from "./deleteModal";
import {
  DeleteModalType,
  hideHeaderProfileRoutes,
  hideHeaderSidebarToggleRoutes,
  hideHeaderToggleRoutes,
} from "../constants/utlis";
import HeaderUserDropdownIcon from "../icons/HeaderUserDropdownIcon";
import SelectOwnerRoleModal from "../core/components/modals/selectOwnerRoleModal";
import { getNotifications } from "../core/data/redux/notification/_notificationAction";
import { notificationTimeFormat } from "../constants/date";
import { headerMenuItems } from "../constants/mainMenu";
import { resetUnreadCount } from "../core/data/redux/notification/notificationSlice";
import { getUserProfile } from "../core/data/redux/user/_userAction";

interface HeaderProps {
  onToggleSidebar: () => void;
  isSidebarOpen: boolean;
}

const Header: React.FC<HeaderProps> = ({ onToggleSidebar, isSidebarOpen }) => {
  const routes: any = all_routes;
  const headerRef = useRef(null);
  const location = useLocation();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [modalType, setModalType] = useState<DeleteModalType>("deleteAccount");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showSelectRoleModal, setShowSelectRoleModal] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] =
    useState(false);
  const isRealEstate = useSelector((state: any) => state.user.isRealEstate);
  const notificationData = useSelector((state: any) => state.notification);
  const notificationList = notificationData?.notificationList || [];
  const notificationCount = notificationData?.totalCount || 0;
  const unreadCount = useSelector(
    (state: any) => state.notification.unreadCount
  );
  console.log("unreadCount ==== ", unreadCount);

  // Get user data from Redux store
  const user = useSelector((state: any) => state.user.userDetail, shallowEqual);
  const userName = useMemo(() => {
    return user
      ? `${user.first_name || ""} ${user.last_name || ""}`.trim()
      : "";
  }, [user?.first_name, user?.last_name]);

  const userEmail = useMemo(() => user?.email || "", [user?.email]);
  const userRole = useMemo(() => user?.role || "", [user?.role]);
  const userImage = useMemo(() => user?.image || "", [user?.image]);

  // Check if the current route is pricing, payment-success, or payment-failed
  const hideElements = hideHeaderToggleRoutes.includes(location.pathname);

  // hide sidebar toggle
  const hideSidebarToggle = hideHeaderSidebarToggleRoutes.includes(
    location.pathname
  );

  // Check if the current route is pricing, payment-success, or payment-failed
  const hideProfileButton = hideHeaderProfileRoutes.includes(location.pathname);

  const hasFetchedUser = useRef(false);

  const fetchUserData = useCallback(() => {
    const isLogged = isLoginUser(); // Assuming isLoginUser is defined somewhere

    if (isLogged && !hasFetchedUser.current) {
      hasFetchedUser.current = true;
      // You can dispatch actions here
      dispatch(getUserProfile({})); // Dispatch action to fetch user profile
      const stored = localStorage.getItem("isRealEstate");
      dispatch(setIsRealEstate(stored !== null ? JSON.parse(stored) : null));
      dispatch(getNotifications({}));
      dispatch(setLogin(true));
    }
  }, [dispatch]); // Dependencies: The function is recreated if 'dispatch' changes.

  useEffect(() => {
    fetchUserData(); // Trigger the memoized function
  }, [fetchUserData]);

  useEffect(() => {
    if (
      isRealEstate === null &&
      (location.pathname === routes.dashboard ||
        location.pathname === routes.nonRealDashboard)
    ) {
      setShowSelectRoleModal(true);
    } else {
      setShowSelectRoleModal(false);
    }
  }, [
    isRealEstate,
    location.pathname,
    routes.dashboard,
    routes.nonRealDashboard,
  ]);

  // Listen for storage changes to update isRealEstate in Redux
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "isRealEstate") {
        const newValue =
          event.newValue !== null ? JSON.parse(event.newValue) : null;
        dispatch(setIsRealEstate(newValue));
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [dispatch]);

  const handelRealEstateToggle = () => {
    const newValue = !isRealEstate;
    dispatch(setIsRealEstate(newValue));
    setStorageItem("isRealEstate", JSON.stringify(newValue));

    if (userRole == "owner" && newValue) {
      navigate(routes.dashboard);
    }
    if (userRole == "owner" && !newValue) {
      navigate(routes.nonRealDashboard);
    }
  };

  const handleRoleSave = (data: any) => {
    if (userRole == "owner" && data?.isRealEstate) {
      navigate(routes.dashboard);
    }
    if (userRole == "owner" && !data?.isRealEstate) {
      navigate(routes.nonRealDashboard);
    }
    setShowSelectRoleModal(false);
  };

  const handleOpenLogoutModal = () => {
    setShowLogoutModal(true);
    setShowDeleteModal(false);
    setShowUserDropdown(false);
  };
  const handleCloseLogoutModal = () => setShowLogoutModal(false);

  const handleOpenDeleteModal = (type: DeleteModalType) => {
    setModalType(type);
    setShowDeleteModal(true);
    setShowLogoutModal(false);
    setShowUserDropdown(false);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  const onViewAllClick = () => {
    setShowNotificationDropdown(false);
    navigate(routes.notifications);
  };

  // Reset when dropdown opens
  const handleNotificationToggle = (isOpen: boolean) => {
    setShowNotificationDropdown(isOpen);
    if (isOpen) {
      dispatch(resetUnreadCount());
      // Optional: refetch full list
      dispatch(getNotifications({}));
    }
  };

  return (
    <>
      <header
        ref={headerRef}
        className={
          location.pathname.includes(routes.home)
            ? "header header-trans"
            : "header header-sticky"
        }
      >
        <div className="container-fluid headerInner">
          <div className="navbar-header">
            {/* <Link to={`/dashboard`} className="navbar-brand logo"> */}
            <div className="navbar-brand logo">
              {true ? (
                <ImageWithBasePath
                  src="assets/img/logo.svg"
                  className="img-fluid"
                  alt="Logo"
                />
              ) : (
                <ImageWithBasePath
                  src="assets/img/logo.svg"
                  className="img-fluid"
                  alt="Another Image"
                />
              )}
            </div>
            {/* </Link> */}

            {/* Render button only if onToggleSidebar is provided */}
            {!hideSidebarToggle && (
              <button className="toggle-btn me-2" onClick={onToggleSidebar}>
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <mask
                    id="mask0_102_1521"
                    style={{ maskType: "alpha" }}
                    maskUnits="userSpaceOnUse"
                    x="0"
                    y="0"
                    width="24"
                    height="24"
                  >
                    <rect width="24" height="24" fill="#D9D9D9" />
                  </mask>
                  <g mask="url(#mask0_102_1521)">
                    <path
                      d="M3.21484 6.8365V4.83594H21.2199V6.8365H3.21484ZM3.21484 18.8398V16.8393H21.2199V18.8398H3.21484ZM3.21484 12.8382V10.8376H21.2199V12.8382H3.21484Z"
                      fill="#0A012E"
                    />
                  </g>
                </svg>
              </button>
            )}
          </div>

          {/* <nav  className="navbar navbar-expand-lg header-nav px-0"> */}
          <Navbar
            collapseOnSelect
            expand="lg"
            className="navbar header-nav px-0"
          >
            {/* <Navbar.Toggle aria-controls="basic-navbar-nav" /> */}
            {/* <Navbar.Collapse id="responsive-navbar-nav">
              <div className="d-flex flex-wrap align-items-center main-menu-wrapper">
                <ul className="main-nav">
                  {headerMenuItems.map((item, index) => (
                    <li key={index}>
                      <NavLink
                        to={item.path}
                        className={({ isActive }) =>
                          `cursor-pointer mx-1 transition-colors ${
                            isActive
                              ? "bg-black text-white"
                              : "hover:text-blue-600"
                          }`
                        }
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            </Navbar.Collapse> */}

            <ul className="nav header-navbar-rht">
              <li className="nav-item gap-4 align-items-center">
                {!hideElements && userRole == "owner" && (
                  <div className="switchBtn d-flex">
                    <div className="toggle-container">
                      <span className={`label ${isRealEstate ? "active" : ""}`}>
                        Real Estate Hours
                      </span>

                      <label className="switch">
                        <input
                          type="checkbox"
                          checked={isRealEstate}
                          onChange={() => handelRealEstateToggle()}
                        />
                        <span className="slider"></span>
                      </label>

                      <span
                        className={`label ${!isRealEstate ? "active" : ""}`}
                      >
                        Non-Real Estate Hours
                      </span>
                    </div>
                  </div>
                )}

                <div className="dropdownWrapper d-flex gap-3">
                  {!hideElements && (
                    <Dropdown
                      className="notificationDropdown"
                      show={showNotificationDropdown}
                      // onToggle={() => setShowNotificationDropdown(!showNotificationDropdown)}
                      onToggle={handleNotificationToggle}
                    >
                      <Dropdown.Toggle variant="default" id="dropdown-basic">
                        <BellIcon />
                        {unreadCount > 0 && (
                          <span
                            className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                            style={{ fontSize: "0.65rem" }}
                          >
                            {unreadCount > 99 ? "99+" : unreadCount}
                          </span>
                        )}
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <div className="notifications">
                          {notificationList?.length === 0 ? (
                            <div className="p-4 text-center text-muted small">
                              No notifications yet
                            </div>
                          ) : (
                            <ul>
                              {notificationList.map((notif: any) => (
                                <li className="d-flex gap-3 align-items-start">
                                  <div className="icon">
                                    <RingGreenIcon />
                                  </div>
                                  <div className="title_message">
                                    <h3>{notif.title}</h3>
                                    <p>{notif.message} </p>
                                  </div>
                                  <div className="time">
                                    {notificationTimeFormat(notif.created_at)}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}

                          {notificationCount > 0 && (
                            <p className="mt-3 mb-0 p-1 d-flex justify-content-end">
                              <Button
                                className="notificationBtn"
                                onClick={() => onViewAllClick()}
                              >
                                View All <LinkIcon />
                              </Button>
                            </p>
                          )}
                        </div>
                      </Dropdown.Menu>
                    </Dropdown>
                  )}

                  {!hideProfileButton && (
                    <Dropdown
                      className="userDropdown"
                      show={showUserDropdown}
                      onToggle={() => setShowUserDropdown(!showUserDropdown)}
                    >
                      <Dropdown.Toggle variant="default" id="dropdown-basic">
                        <span className="ellipsText">{userName}</span>
                        <HeaderUserDropdownIcon />
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <div className="profiledetail">
                          <div className="userDetail d-flex gap-3">
                            <div className="userImg">
                              <img
                                src={
                                  userImage
                                    ? `${userImage}`
                                    : "/assets/img/userCircle.png"
                                }
                              />
                            </div>
                            <div className="">
                              <h3>{userName}</h3>
                              <label>{userEmail}</label>
                            </div>
                          </div>
                          <ul>
                            <li>
                              <Link
                                to={routes.profile}
                                onClick={() => setShowUserDropdown(false)}
                              >
                                <UserIcon /> Profile setting
                              </Link>
                            </li>
                            <li>
                              <Link
                                to={routes.profileChangePassword}
                                onClick={() => setShowUserDropdown(false)}
                              >
                                <LockIcon />
                                Change Password
                              </Link>
                            </li>

                            {userRole == "owner" && (
                              <li>
                                <button
                                  onClick={() =>
                                    handleOpenDeleteModal("deleteAccount")
                                  }
                                >
                                  <DeleteGreyIcon /> Delete Account
                                </button>
                              </li>
                            )}
                          </ul>
                          <button onClick={handleOpenLogoutModal}>
                            <LogoutIcon /> Log out
                          </button>
                        </div>
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </div>
              </li>
            </ul>
            {/* </nav> */}
          </Navbar>
        </div>
      </header>

      <DeleteModal
        show={showDeleteModal}
        onHide={handleCloseDeleteModal}
        type={modalType}
      />

      <LogoutModal show={showLogoutModal} onHide={handleCloseLogoutModal} />

      <SelectOwnerRoleModal
        show={showSelectRoleModal}
        onHide={() => setShowSelectRoleModal(false)}
        onSave={handleRoleSave}
      />
    </>
  );
};

export default Header;
